import { CircleInfoIcon, PlusIcon } from "@xola/icons";
import { Alert, Button as UIButton } from "@xola/ui-kit";
import React, { useState } from "react";
import { Virtuoso } from "react-virtuoso";
import { Button, ButtonType, CheckoutType } from "types";
import { SearchInput } from "../../Common/SearchInput";
import { ErrorBoundary } from "../../ErrorBoundary";
import { ButtonItem } from "./ButtonItem";
import { MultipleItemCheckoutModal } from "./MultipleItemCheckoutModal/MultipleItemCheckoutModal";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";

interface ButtonListProps {
    type: ButtonType;
    checkoutType: string;
    popupType: string;
}

export const ButtonList = ({ type, checkoutType, popupType }: ButtonListProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedEditButton, setSelectedEditButton] = useState<Button>();
    const [isOpenMultiModal, setIsOpenMultiModal] = useState(false);
    const { data: buttons, isLoading, fetchNextPage, hasNextPage } = useInfiniteCollection<Button>("buttons", { type });

    const filteredButtons = buttons.filter((button) => {
        return !button.archivedAt && (searchTerm ? button.name.toLowerCase().includes(searchTerm) : true);
    });

    if (checkoutType === CheckoutType.EMBEDDED && type === ButtonType.GIFT) {
        return (
            <Alert color="warning" className="py-5 px-10">
                <CircleInfoIcon className="relative top-[-1px]" /> This checkout style is not currently supported as an
                embed
            </Alert>
        );
    }

    const handleCloseModal = () => {
        setIsOpenMultiModal(false);

        setTimeout(() => {
            setSelectedEditButton(undefined);
        }, 500);
    };

    const handleOpenModal = (button: Button) => {
        setSelectedEditButton(button);
        setIsOpenMultiModal(true);
    };

    return (
        <div className="space-y-4 px-10">
            <div className="flex items-center justify-between">
                <p className="text-md">Saved Buttons</p>
                <UIButton icon={<PlusIcon />} onClick={() => setIsOpenMultiModal(true)}>
                    Create New Button
                </UIButton>
            </div>
            <div>
                <SearchInput value={searchTerm} onChange={(searchResult: string) => setSearchTerm(searchResult)} />
            </div>
            <div>
                {filteredButtons?.length > 0 && !isLoading ? (
                    <Virtuoso
                        style={{ height: 400 }}
                        data={filteredButtons}
                        endReached={async () => {
                            if (hasNextPage) {
                                fetchNextPage();
                            }
                        }}
                        itemContent={(index, button) => {
                            return (
                                <ErrorBoundary fallbackMessage={`Error rendering button ${button.id}`}>
                                    <ButtonItem
                                        key={button.id}
                                        button={button}
                                        checkoutType={checkoutType}
                                        popupType={popupType}
                                        type={type}
                                        onClick={() => handleOpenModal(button)}
                                    />
                                </ErrorBoundary>
                            );
                        }}
                    />
                ) : (
                    <span className="flex items-center justify-center pt-10">
                        No Buttons found. Click the button above to create a new button
                    </span>
                )}
            </div>
            <MultipleItemCheckoutModal
                selectedEditButton={selectedEditButton}
                isOpen={isOpenMultiModal}
                buttonType={type}
                onClose={handleCloseModal}
            />
        </div>
    );
};
